import React from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

// Plugin to add percentage labels just outside donut segments
const percentageLabelsPlugin = {
  id: 'percentageLabels',
  afterDatasetsDraw: (chart: any) => {
    const { ctx, data } = chart;
    const total = data.datasets[0].data.reduce((sum: number, value: number) => sum + value, 0);
    
    if (total === 0) return;
    
    ctx.save();
    
    data.datasets[0].data.forEach((value: number, index: number) => {
      const percentage = Math.round((value / total) * 100);
      
      // Skip labels for very small segments (less than 5%)
      if (percentage < 5) return;
      
      const meta = chart.getDatasetMeta(0);
      const arc = meta.data[index];
      
      if (arc) {
        const { x, y, outerRadius } = arc;
        const midAngle = (arc.startAngle + arc.endAngle) / 2;
        
        // Position labels just outside the donut ring
        const labelRadius = outerRadius + 35;
        const labelX = x + Math.cos(midAngle) * labelRadius;
        const labelY = y + Math.sin(midAngle) * labelRadius;
        
        // Style the text
        ctx.fillStyle = 'black';
        ctx.font = 'bold 22px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
        ctx.shadowBlur = 1;
        
        // Draw the percentage text
        ctx.fillText(`${percentage}%`, labelX, labelY);
      }
    });
    
    ctx.restore();
  }
};

ChartJS.register(ArcElement, Tooltip, Legend, percentageLabelsPlugin);

interface DonutChartProps {
  successfulTrials: number;
  negativeBalanceTrials: number;
  overdrawnTrials: number;
  className?: string;
}

/**
 * Donut chart component for displaying trial outcome distribution
 */
export const DonutChart: React.FC<DonutChartProps> = ({
  successfulTrials,
  negativeBalanceTrials,
  overdrawnTrials,
  className = ''
}) => {
  const total = successfulTrials + negativeBalanceTrials + overdrawnTrials;

  if (total === 0) {
    return (
      <div className={`donut-chart-container ${className}`}>
        <div className="no-data">No trial data available</div>
      </div>
    );
  }

  const data = {
    labels: ['Successful', 'Negative Balance', 'Overdrawn'],
    datasets: [
      {
        data: [successfulTrials, negativeBalanceTrials, overdrawnTrials],
        backgroundColor: [
          'rgba(5, 150, 105, 0.8)',   // Success - green
          'rgba(220, 38, 38, 0.8)',    // Negative - red
          'rgba(217, 119, 6, 0.8)',    // Overdrawn - orange/warning
        ],
        borderColor: [
          'rgba(5, 150, 105, 1)',
          'rgba(220, 38, 38, 1)',
          'rgba(217, 119, 6, 1)',
        ],
        borderWidth: 2,
      },
    ],
  };

  const options: ChartOptions<'doughnut'> = {
    responsive: false,
    maintainAspectRatio: false,
    cutout: '70%',
    layout: {
      padding: {
        top: 40,
        bottom: 40,
        left: 40,
        right: 40,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} trials (${percentage}%)`;
          },
        },
      },
    },
    animation: {
      animateRotate: true,
      animateScale: true,
    },
  };


  return (
    <div className={`donut-chart-container ${className}`}>
      <div className="donut-chart-wrapper">
        <Doughnut data={data} options={options} width={480} height={280} />
      </div>
      <div className="donut-legend">
        <div className="donut-legend-item">
          <div className="donut-legend-circle" style={{ backgroundColor: 'rgba(5, 150, 105, 0.8)' }}></div>
          <span className="donut-legend-text">Successful</span>
        </div>
        <div className="donut-legend-item">
          <div className="donut-legend-circle" style={{ backgroundColor: 'rgba(220, 38, 38, 0.8)' }}></div>
          <span className="donut-legend-text">Negative Balance</span>
        </div>
        <div className="donut-legend-item">
          <div className="donut-legend-circle" style={{ backgroundColor: 'rgba(217, 119, 6, 0.8)' }}></div>
          <span className="donut-legend-text">Overdrawn</span>
        </div>
      </div>
    </div>
  );
};

